import { vec3 } from "gl-matrix";
import { URLSearchParamsInit } from "react-router-dom";

import { LUMBRIDGE_TD_MAP_X, LUMBRIDGE_TD_MAP_Y } from "../../towerdefense/lumbridgeTdRoute";
import { OsrsMenuEntry } from "../components/rs/menu/OsrsMenu";
import { MenuTargetType } from "../rs/MenuEntry";
import { CacheSystem } from "../rs/cache/CacheSystem";
import { CacheLoaderFactory, getCacheLoaderFactory } from "../rs/cache/loader/CacheLoaderFactory";
import { BasTypeLoader } from "../rs/config/bastype/BasTypeLoader";
import { LocTypeLoader } from "../rs/config/loctype/LocTypeLoader";
import { NpcTypeLoader } from "../rs/config/npctype/NpcTypeLoader";
import { ObjTypeLoader } from "../rs/config/objtype/ObjTypeLoader";
import { SeqTypeLoader } from "../rs/config/seqtype/SeqTypeLoader";
import { SpotAnimTypeLoader } from "../rs/config/spotanimtype/SpotAnimTypeLoader";
import { VarManager } from "../rs/config/vartype/VarManager";
import { MapFileIndex, getMapSquareId } from "../rs/map/MapFileIndex";
import { SeqFrameLoader } from "../rs/model/seq/SeqFrameLoader";
import { Pathfinder } from "../rs/pathfinder/Pathfinder";
import { TextureLoader } from "../rs/texture/TextureLoader";
import { isTouchDevice, isWallpaperEngine } from "../util/DeviceUtil";
import { getAssetBaseUrl } from "../util/PublicUrl";
import { CacheList, LoadedCache } from "./Caches";
import { Camera, CameraView, ProjectionType } from "./Camera";
import { InputManager } from "./InputManager";
import { MapManager } from "./MapManager";
import { MapViewerRenderer } from "./MapViewerRenderer";
import { MapViewerRendererType, createRenderer } from "./MapViewerRenderers";
import { NpcSpawn } from "./data/npc/NpcSpawn";
import { ObjSpawn } from "./data/obj/ObjSpawn";
import { RenderDataWorkerPool } from "./worker/RenderDataWorkerPool";

const DEFAULT_RENDER_DISTANCE = isWallpaperEngine ? 512 : 128;

const CACHED_MAP_IMAGE_PREFIX = `${getAssetBaseUrl()}/map-images/`;
const LUMBRIDGE_CASTLE_CAMERA: CameraView = {
    position: vec3.fromValues(3242, -26, 3202),
    pitch: -245,
    yaw: 1862,
    fov: 100,
    orthoZoom: 15,
};
const PINNED_CAMERA_X_MIN = LUMBRIDGE_TD_MAP_X * 64;
const PINNED_CAMERA_X_MAX = PINNED_CAMERA_X_MIN + 64;
const PINNED_CAMERA_Y_MIN = LUMBRIDGE_CASTLE_CAMERA.position[1] - 72;
const PINNED_CAMERA_Y_MAX = LUMBRIDGE_CASTLE_CAMERA.position[1] + 72;
const PINNED_CAMERA_Z_MIN = LUMBRIDGE_TD_MAP_Y * 64;
const PINNED_CAMERA_Z_MAX = PINNED_CAMERA_Z_MIN + 64;
const PINNED_CAMERA_PITCH_MIN = -512;
const PINNED_CAMERA_PITCH_MAX = 512;

export class MapViewer {
    inputManager: InputManager = new InputManager();
    camera: Camera = new Camera(
        LUMBRIDGE_CASTLE_CAMERA.position[0],
        LUMBRIDGE_CASTLE_CAMERA.position[1],
        LUMBRIDGE_CASTLE_CAMERA.position[2],
        LUMBRIDGE_CASTLE_CAMERA.pitch,
        LUMBRIDGE_CASTLE_CAMERA.yaw,
    );
    readonly pinnedView: boolean = true;

    pathfinder: Pathfinder = new Pathfinder();

    renderer: MapViewerRenderer;

    // Cache
    loadedCache!: LoadedCache;
    cacheSystem!: CacheSystem;
    loaderFactory!: CacheLoaderFactory;

    textureLoader!: TextureLoader;
    seqTypeLoader!: SeqTypeLoader;
    seqFrameLoader!: SeqFrameLoader;

    locTypeLoader!: LocTypeLoader;
    objTypeLoader!: ObjTypeLoader;
    npcTypeLoader!: NpcTypeLoader;
    spotAnimTypeLoader!: SpotAnimTypeLoader;

    basTypeLoader!: BasTypeLoader;

    varManager!: VarManager;

    mapFileIndex!: MapFileIndex;

    isNewTextureAnim: boolean = false;

    // Settings

    // Tile distance
    renderDistance: number = DEFAULT_RENDER_DISTANCE;
    // Map square distance
    unloadDistance: number = 2;
    // Map square distance
    lodDistance: number = 3;

    tooltips: boolean = !isTouchDevice;
    debugId: boolean = false;

    // State
    needsSearchParamUpdate: boolean = false;
    lastTimeSearchParamsUpdated: number = 0;

    menuOpen: boolean = false;
    menuOpenedFrame: number = 0;
    menuX: number = -1;
    menuY: number = -1;
    menuEntries: OsrsMenuEntry[] = [];

    debugText?: string;

    mapImageUrls: Map<number, string> = new Map();
    minimapImageUrls: Map<number, string> = new Map();
    loadingMapImageIds: Set<number> = new Set();

    cameraSpeed: number = 1;

    constructor(
        readonly workerPool: RenderDataWorkerPool,
        readonly cacheList: CacheList,
        readonly objSpawns: ObjSpawn[],
        public npcSpawns: NpcSpawn[],
        readonly mapImageCache: Cache,
        rendererType: MapViewerRendererType,
        cache: LoadedCache,
    ) {
        this.renderer = createRenderer(rendererType, this);
        this.initCache(cache);
    }

    getSearchParams(): URLSearchParamsInit {
        if (this.pinnedView) {
            const params: any = { v: 1 };
            if (this.loadedCache.info.name !== this.cacheList.latest.name) {
                params["cache"] = this.loadedCache.info.name;
            }
            return params;
        }

        const cx = this.camera.getPosX().toFixed(2).toString();
        const cy = -this.camera.getPosY().toFixed(2).toString();
        const cz = this.camera.getPosZ().toFixed(2).toString();

        const yaw = this.camera.yaw & 2047;

        const p = (this.camera.pitch | 0).toString();
        const y = yaw.toString();

        const params: any = {
            cx,
            cy,
            cz,
            p,
            y,
        };

        if (this.camera.projectionType === ProjectionType.ORTHO) {
            params["pt"] = "o";
            params["z"] = this.camera.orthoZoom.toString();
        }

        if (this.loadedCache.info.name !== this.cacheList.latest.name) {
            params["cache"] = this.loadedCache.info.name;
        }

        params["v"] = 1;

        return params;
    }

    applySearchParams(searchParams: URLSearchParams) {
        if (this.pinnedView) {
            const cacheNameParam = searchParams.get("cache");
            if (!cacheNameParam) {
                this.enforcePinnedCamera();
            }
            return;
        }

        const cx = searchParams.get("cx");
        const cy = searchParams.get("cy");
        const cz = searchParams.get("cz");

        const pitch = searchParams.get("p");
        const yaw = searchParams.get("y");

        const v = searchParams.get("v");

        if (searchParams.get("pt") === "o") {
            this.camera.projectionType = ProjectionType.ORTHO;
        }

        const zoom = searchParams.get("z");
        if (zoom) {
            this.camera.orthoZoom = parseInt(zoom);
        }

        if (cx && cy && cz) {
            const pos = vec3.fromValues(parseFloat(cx), -parseFloat(cy), parseFloat(cz));
            this.camera.pos = pos;
        }
        if (pitch) {
            this.camera.pitch = parseInt(pitch);
            if (!v) {
                this.camera.pitch = -this.camera.pitch;
            }
        }
        if (yaw) {
            this.camera.yaw = parseInt(yaw);
            if (!v) {
                this.camera.yaw = 2048 - this.camera.yaw;
            }
        }
    }

    init(): void {
        this.enforcePinnedCamera();
        this.workerPool.loadCachedMapImages().then((mapImageUrls) => {
            mapImageUrls.forEach((value, key) => this.mapImageUrls.set(key, value));
        });
    }

    initCache(cache: LoadedCache): void {
        this.loadedCache = cache;
        this.cacheSystem = CacheSystem.fromFiles(cache.type, cache.files);
        this.loaderFactory = getCacheLoaderFactory(cache.info, this.cacheSystem);
        this.workerPool.initCache(cache, this.objSpawns, this.npcSpawns);
        this.clearMapImageUrls();

        this.textureLoader = this.loaderFactory.getTextureLoader();
        this.seqTypeLoader = this.loaderFactory.getSeqTypeLoader();
        this.seqFrameLoader = this.loaderFactory.getSeqFrameLoader();
        this.locTypeLoader = this.loaderFactory.getLocTypeLoader();
        this.objTypeLoader = this.loaderFactory.getObjTypeLoader();
        this.npcTypeLoader = this.loaderFactory.getNpcTypeLoader();
        this.spotAnimTypeLoader = this.loaderFactory.getSpotAnimTypeLoader();
        this.basTypeLoader = this.loaderFactory.getBasTypeLoader();

        this.varManager = new VarManager(this.loaderFactory.getVarBitTypeLoader());
        const questTypeLoader = this.loaderFactory.getQuestTypeLoader();
        if (questTypeLoader) {
            this.varManager.setQuestsCompleted(questTypeLoader);
        }

        const mapFileLoader = this.loaderFactory.getMapFileLoader();
        this.mapFileIndex = mapFileLoader.mapFileIndex;

        this.isNewTextureAnim = cache.info.game === "runescape" && cache.info.revision >= 681;

        this.enforcePinnedCamera();
        this.renderer.initCache();

        this.updateSearchParams();
    }

    setRenderer(renderer: MapViewerRenderer): void {
        this.renderer = renderer;
        this.renderer.initCache();
        this.resetMenu();
    }

    /**
     * Sets the camera position to a new arbitrary position
     * @param newView Any of the items you want to move: Position, pitch, yaw
     */
    setCamera(newView: Partial<CameraView>): void {
        if (this.pinnedView) {
            if (newView.position) {
                vec3.copy(this.camera.pos, newView.position);
            }
            if (newView.pitch !== undefined) {
                this.camera.pitch = newView.pitch;
            }
            if (newView.yaw !== undefined) {
                this.camera.yaw = newView.yaw;
            }
            if (newView.fov !== undefined) {
                this.camera.fov = newView.fov;
            }
            if (newView.orthoZoom !== undefined) {
                this.camera.orthoZoom = newView.orthoZoom;
            }
            this.clampPinnedCamera();
            return;
        }

        if (newView.position) {
            vec3.copy(this.camera.pos, newView.position);
        }
        if (newView.pitch !== undefined) {
            this.camera.pitch = newView.pitch;
        }
        if (newView.yaw !== undefined) {
            this.camera.yaw = newView.yaw;
        }
        if (newView.fov !== undefined) {
            this.camera.fov = newView.fov;
        }
        if (newView.orthoZoom !== undefined) {
            this.camera.orthoZoom = newView.orthoZoom;
        }
        this.camera.updated = true;
    }

    enforcePinnedCamera(): void {
        vec3.copy(this.camera.pos, LUMBRIDGE_CASTLE_CAMERA.position);
        this.camera.pitch = LUMBRIDGE_CASTLE_CAMERA.pitch;
        this.camera.yaw = LUMBRIDGE_CASTLE_CAMERA.yaw;
        this.camera.fov = LUMBRIDGE_CASTLE_CAMERA.fov;
        this.camera.updated = true;
    }

    clampPinnedCamera(): void {
        this.camera.pos[0] = Math.max(
            PINNED_CAMERA_X_MIN,
            Math.min(PINNED_CAMERA_X_MAX, this.camera.pos[0]),
        );
        this.camera.pos[1] = Math.max(
            PINNED_CAMERA_Y_MIN,
            Math.min(PINNED_CAMERA_Y_MAX, this.camera.pos[1]),
        );
        this.camera.pos[2] = Math.max(
            PINNED_CAMERA_Z_MIN,
            Math.min(PINNED_CAMERA_Z_MAX, this.camera.pos[2]),
        );
        this.camera.pitch = Math.max(
            PINNED_CAMERA_PITCH_MIN,
            Math.min(PINNED_CAMERA_PITCH_MAX, this.camera.pitch),
        );
        this.camera.yaw = ((this.camera.yaw % 2048) + 2048) % 2048;
        this.camera.updated = true;
    }

    resetPinnedCamera(): void {
        vec3.copy(this.camera.pos, LUMBRIDGE_CASTLE_CAMERA.position);
        this.camera.pitch = LUMBRIDGE_CASTLE_CAMERA.pitch;
        this.camera.yaw = LUMBRIDGE_CASTLE_CAMERA.yaw;
        this.camera.fov = LUMBRIDGE_CASTLE_CAMERA.fov;
        this.camera.updated = true;
    }

    updateSearchParams(): void {
        this.needsSearchParamUpdate = true;
        this.lastTimeSearchParamsUpdated = performance.now();
    }

    closeMenu = () => {
        this.menuOpen = false;
        this.menuX = -1;
        this.menuY = -1;
        this.renderer.canvas.focus();
    };

    resetMenu = () => {
        this.closeMenu();
        this.menuOpenedFrame = 0;
    };

    onExamine = (entry: OsrsMenuEntry) => {
        let lookupType: string | undefined;
        switch (entry.targetType) {
            case MenuTargetType.NPC:
                lookupType = "npc";
                break;
            case MenuTargetType.LOC:
                lookupType = "object";
                break;
            case MenuTargetType.OBJ:
                lookupType = "item";
                break;
        }
        if (lookupType) {
            window.open(
                `https://oldschool.runescape.wiki/w/Special:Lookup?type=${lookupType}&id=${entry.targetId}`,
                "_blank",
            );
        }
        this.closeMenu();
    };

    updateVars(): void {
        this.workerPool.setVars(this.varManager.values);
    }

    static getCachedMapImageUrl(mapX: number, mapY: number): string {
        return CACHED_MAP_IMAGE_PREFIX + `${mapX}_${mapY}.png`;
    }

    async queueLoadMapImage(mapX: number, mapY: number) {
        const mapManager = this.renderer.mapManager;
        const mapId = getMapSquareId(mapX, mapY);
        if (
            this.loadingMapImageIds.size > this.workerPool.size * 4 ||
            this.mapImageUrls.has(mapId) ||
            this.loadingMapImageIds.has(mapId) ||
            mapManager.invalidMapIds.has(mapId) ||
            mapManager.loadingMapIds.has(mapId)
        ) {
            return;
        }
        this.loadingMapImageIds.add(mapId);

        const minimapData = await this.workerPool.queueMapImage(mapX, mapY, 0, true);
        if (minimapData) {
            const url = URL.createObjectURL(minimapData.minimapBlob);
            this.setMapImageUrl(mapX, mapY, url, false);
        } else {
            mapManager.invalidMapIds.add(mapId);
        }

        this.loadingMapImageIds.delete(mapId);
    }

    getMapImageUrl(mapX: number, mapY: number, minimap: boolean): string | undefined {
        if (mapX < 0 || mapY < 0 || mapX >= MapManager.MAX_MAP_X || mapY >= MapManager.MAX_MAP_Y) {
            return undefined;
        }
        const urls = minimap ? this.minimapImageUrls : this.mapImageUrls;
        if (minimap) {
            this.renderer.mapManager.loadMap(mapX, mapY);
        } else {
            this.queueLoadMapImage(mapX, mapY);
        }
        const mapId = getMapSquareId(mapX, mapY);
        return urls.get(mapId);
    }

    setMapImageUrl(
        mapX: number,
        mapY: number,
        url: string,
        minimap: boolean,
        cache: boolean = true,
    ): void {
        const mapId = getMapSquareId(mapX, mapY);
        const urls = minimap ? this.minimapImageUrls : this.mapImageUrls;
        const old = urls.get(mapId);
        if (old) {
            URL.revokeObjectURL(old);
        }
        if (cache) {
            fetch(url).then((resp) => {
                if (resp.ok) {
                    const request = new Request(MapViewer.getCachedMapImageUrl(mapX, mapY), {
                        headers: {
                            "RS-Cache-Name": this.loadedCache.info.name,
                        },
                    });
                    this.mapImageCache.put(request, resp);
                }
            });
        }
        urls.set(mapId, url);
    }

    clearMapImageUrls(): void {
        for (const url of this.mapImageUrls.values()) {
            URL.revokeObjectURL(url);
        }
        for (const url of this.minimapImageUrls.values()) {
            URL.revokeObjectURL(url);
        }
        this.mapImageUrls.clear();
        this.minimapImageUrls.clear();
        this.loadingMapImageIds.clear();
    }
}
